import {
	AmbientLight,
	DirectionalLight,
	HemisphereLight,
	Light,
	LineSegments,
	Mesh,
	PointLight,
	RectAreaLight,
	Scene,
	SpotLight
} from 'three';

/**
 * Render quality level (0-10)
 * 0 = wireframe
 * 1 = lowest quality
 * 10 = max quality
 */
export type RenderQuality = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

/**
 * All supported light types for visualization
 */
export type VisualizableLight =
	| AmbientLight
	| DirectionalLight
	| HemisphereLight
	| PointLight
	| RectAreaLight
	| SpotLight;

/**
 * Light type enum for identification
 */
export enum LightType {
	Ambient = 'AmbientLight',
	Directional = 'DirectionalLight',
	Hemisphere = 'HemisphereLight',
	Point = 'PointLight',
	RectArea = 'RectAreaLight',
	Spot = 'SpotLight',
	Unknown = 'Unknown'
}

/**
 * Get the light type for a given light
 */
export const getLightType = (light: Light): LightType => {
	if (light instanceof AmbientLight) return LightType.Ambient;
	if (light instanceof DirectionalLight) return LightType.Directional;
	if (light instanceof HemisphereLight) return LightType.Hemisphere;
	if (light instanceof PointLight) return LightType.Point;
	if (light instanceof RectAreaLight) return LightType.RectArea;
	if (light instanceof SpotLight) return LightType.Spot;
	return LightType.Unknown;
};

/**
 * Base objects that represent a light's visualization
 * All light types have at least a sphere
 */
export interface LightHelperObjectsBase {
	/** Primary sphere representing the light */
	sphere: Mesh;
}

/**
 * Objects for SpotLight visualization (cone + sphere + line)
 */
export interface SpotLightHelperObjects extends LightHelperObjectsBase {
	/** Cone showing light direction */
	cone: Mesh;
	/** Line from light to target */
	line: LineSegments;
}

/**
 * Objects for DirectionalLight visualization (sphere + line)
 */
export interface DirectionalLightHelperObjects extends LightHelperObjectsBase {
	/** Line showing light direction */
	line: LineSegments;
}

/**
 * Objects for PointLight visualization (sphere only)
 */
export interface PointLightHelperObjects extends LightHelperObjectsBase {
	// Just sphere for now
}

/**
 * Objects for AmbientLight visualization (sphere only)
 */
export interface AmbientLightHelperObjects extends LightHelperObjectsBase {
	// Just sphere for now
}

/**
 * Objects for HemisphereLight visualization
 * Includes sky sphere, ground sphere, connecting axis, gradient rings, and direction arrows
 */
export interface HemisphereLightHelperObjects extends LightHelperObjectsBase {
	/** Sky sphere at top (uses sky color) - interactive for color picking */
	skySphere: Mesh;
	/** Ground sphere at bottom (uses ground color) - interactive for color picking */
	groundSphere: Mesh;
	/** Line connecting sky and ground spheres (vertical axis) */
	axisLine: LineSegments;
	/** 5 gradient rings showing color blend from sky to ground (thickness = intensity) */
	gradientRings: Mesh[];
	/** Arrow pointing down from sky sphere */
	skyArrow: Mesh;
	/** Arrow pointing up from ground sphere */
	groundArrow: Mesh;
	/** Control sphere at center - click toggles on/off, drag changes intensity */
	controlSphere: Mesh;
}

/**
 * Objects for RectAreaLight visualization (sphere only)
 */
export interface RectAreaLightHelperObjects extends LightHelperObjectsBase {
	// Just sphere for now
}

/**
 * Union type for all light helper objects
 */
export type LightHelperObjects =
	| SpotLightHelperObjects
	| DirectionalLightHelperObjects
	| PointLightHelperObjects
	| AmbientLightHelperObjects
	| HemisphereLightHelperObjects
	| RectAreaLightHelperObjects;

/**
 * Type guard to check if helper objects have a cone (SpotLight)
 */
export const hasConeMesh = (objects: LightHelperObjects): objects is SpotLightHelperObjects => {
	return 'cone' in objects && objects.cone !== undefined;
};

/**
 * Type guard to check if helper objects have a line
 */
export const hasLineMesh = (
	objects: LightHelperObjects
): objects is SpotLightHelperObjects | DirectionalLightHelperObjects => {
	return 'line' in objects && objects.line !== undefined;
};

/**
 * Type guard to check if helper objects are for HemisphereLight (has sky and ground spheres)
 */
export const isHemisphereLightObjects = (
	objects: LightHelperObjects
): objects is HemisphereLightHelperObjects => {
	return 'skySphere' in objects && 'groundSphere' in objects;
};

/**
 * Configuration for LightHelper
 */
export interface LightHelperConfig {
	/** Scene to add helper objects to */
	scene: Scene;
	/** Cone radius (default: 3.75) */
	coneRadius?: number;
	/** Cone height (default: 22.5) */
	coneHeight?: number;
	/** Whether helper is initially visible (default: false) */
	visible?: boolean;
	/** Render quality 0-10 (default: 5) */
	quality?: RenderQuality;
}

/**
 * Configuration for LightHelperManager
 */
export interface LightHelperManagerConfig {
	/** Scene to traverse for lights */
	scene: Scene;
	/** Global cone radius (default: 3.75) */
	coneRadius?: number;
	/** Global cone height (default: 22.5) */
	coneHeight?: number;
	/** Initial visibility state (default: false) */
	visible?: boolean;
	/** Render quality 0-10 (default: 5) */
	quality?: RenderQuality;
}

/**
 * Default values for light helper configuration
 */
export const LIGHT_HELPER_DEFAULTS = {
	CONE_RADIUS: 3.75,
	CONE_HEIGHT: 22.5,
	SPHERE_COLOR_ON: 0xffff00, // Yellow when light is on
	SPHERE_COLOR_OFF: 0x333333, // Dark grey when light is off
	CONE_COLOR_OFF: 0x666666, // Dim grey when light is off
	QUALITY: 5 as RenderQuality
} as const;

/**
 * Get geometry segment counts based on quality level
 * @param quality - Quality level 0-10
 * @returns Object with radialSegments and heightSegments for cone/sphere
 */
export const getQualitySegments = (quality: RenderQuality): { radial: number; height: number } => {
	if (quality === 0) {
		// Wireframe mode - minimal segments
		return { radial: 4, height: 1 };
	}
	// Scale from 4 (quality 1) to 32 (quality 10)
	const radial = Math.round(4 + ((quality - 1) * 28) / 9);
	const height = Math.max(1, Math.round(quality / 2));
	return { radial, height };
};

